import axios, { AxiosResponseHeaders, RawAxiosResponseHeaders } from 'axios';

import { APIUrlGenerator } from '@/lib/helper';
import { getAxiosTimeout } from '@/lib/axios-config';

import { ARCHIVE_PAGE_TYPE, FILTER_KEYS, SORT_MODE } from '@/components/app/archive/helpers';
import {
  ArchivePageType,
  FilterParamsType,
  getAdsBannerType,
  GetArchiveInfoType,
} from '@/components/app/archive/services/type';
import { HasPageParam } from '@/components/shared/services/types';

import { API_ROUTES } from '@/constant/routes';
import ArchiveAdsBannerModel from '@/models/archive-ads-banner.model';
import CategoryModel from '@/models/category.model';
import ProductModel from '@/models/product.model';
import TagModel from '@/models/tag.model';

export async function getArchiveInfo(
  slug: GetArchiveInfoType,
  type: ArchivePageType = ARCHIVE_PAGE_TYPE.CATEGORY,
) {
  const route =
    type === ARCHIVE_PAGE_TYPE.CATEGORY
      ? API_ROUTES.GET_CATEGORY_INFO(slug)
      : API_ROUTES.GET_TAG_INFO(slug);
  const {
    data: { payload },
  }: { data: { payload: CategoryModel | TagModel } } = await axios.get(APIUrlGenerator(route), {
    timeout: getAxiosTimeout(), // Dynamic timeout: 60s for build, 30s for runtime
  });
  return payload;
}

export async function getProductList(pageParam?: HasPageParam<FilterParamsType>) {
  const PRODUCTS_PER_PAGE = 12;

  const sellable = !(pageParam && pageParam[FILTER_KEYS.SELLABLE] === false);
  const isSearchPage = !!(pageParam && pageParam[FILTER_KEYS.FIND]);

  const requestFilters: any = {};

  if (pageParam) {
    Object.keys(pageParam).forEach((key) => {
      if (
        key !== FILTER_KEYS.SORT &&
        key !== FILTER_KEYS.SELLABLE &&
        key !== FILTER_KEYS.MOST_RELEVANT
      ) {
        requestFilters[key] = pageParam[key];
      }
    });
  }

  requestFilters[FILTER_KEYS.SELLABLE] = sellable;

  const isMostRelevant = pageParam && pageParam[FILTER_KEYS.MOST_RELEVANT] === true;

  if (!isMostRelevant && pageParam && pageParam[FILTER_KEYS.SORT]) {
    requestFilters[FILTER_KEYS.SORT] = pageParam[FILTER_KEYS.SORT];
  } else if (!isMostRelevant) {
    if (!isSearchPage && pageParam && (pageParam?.tag_slugs || pageParam?.category_slugs)) {
      requestFilters[FILTER_KEYS.SORT] = SORT_MODE.MOST_UPDATE;
    }
  }
  try {
    const response: {
      data: { payload: ProductModel[] };
      headers: RawAxiosResponseHeaders | AxiosResponseHeaders;
    } = await axios.get(APIUrlGenerator(API_ROUTES.GET_PRODUCT_LIST), {
      params: { ...requestFilters, 'per-page': PRODUCTS_PER_PAGE },
      timeout: getAxiosTimeout(), // Dynamic timeout: 60s for build, 30s for runtime
    });

    const currentPage = parseInt(response.headers['x-pagination-current-page'], 10);
    const totalProducts = parseInt(response.headers['x-pagination-total-count'], 10);

    const totalPages = Math.ceil(totalProducts / PRODUCTS_PER_PAGE);

    return {
      data: response.data.payload,
      previousCursor: currentPage > 1 ? currentPage - 1 : null,
      nextCursor: currentPage < totalPages ? currentPage + 1 : null,
      currentPage: currentPage,
      totalPages: totalPages,
    };
  } catch (error) {
    return {
      data: [],
      previousCursor: null,
      nextCursor: null,
      currentPage: 1,
      totalPages: 1,
      error: error instanceof Error ? error.message : 'Failed to fetch products',
    };
  }
}

export async function getSuggestionDemosList(
  slug: GetArchiveInfoType,
  type: ArchivePageType = ARCHIVE_PAGE_TYPE.CATEGORY,
) {
  const params =
    type === ARCHIVE_PAGE_TYPE.CATEGORY
      ? { [FILTER_KEYS.CATEGORY_SLUGS]: slug }
      : { [FILTER_KEYS.TAG_SLUGS]: slug };
  const {
    data: { payload },
  }: { data: { payload: ProductModel[] } } = await axios.get(
    APIUrlGenerator(API_ROUTES.GET_SUGGESTION_DEMOS_LIST),
    { 
      params: params,
      timeout: getAxiosTimeout(), // Dynamic timeout: 60s for build, 30s for runtime
    },
  );
  return payload;
}

export async function getFilterCategoryList() {
  const {
    data: { payload },
  }: { data: { payload: CategoryModel[] } } = await axios.get(
    APIUrlGenerator(API_ROUTES.GET_FILTER_CATEGORY_LIST),
    {
      timeout: getAxiosTimeout(), // Dynamic timeout: 60s for build, 30s for runtime
    },
  );
  return payload;
}

export async function getArchiveAdsBanner(id: getAdsBannerType) {
  const {
    data: { payload },
  }: { data: { payload: ArchiveAdsBannerModel } } = await axios.get(
    APIUrlGenerator(API_ROUTES.GET_ARCHIVE_ADS_BANNER(id)),
    {
      timeout: getAxiosTimeout(), // Dynamic timeout: 60s for build, 30s for runtime
    },
  );
  return payload;
}
